import { debounce } from 'lodash';
import { computeDiff } from '../collab/diffEngine';
import { isApplyingRemote } from '../collab/remoteGuard';
import {
  isApplyingUndo,
  isEphemeralMutation,
  isVariablesView,
  focusFromDiff,
} from './undoHistory';
import { docIdOf, isStableEditState } from '../collab/localEditGate';

// Subscriber qui empile l'inverse des mutations LOCALES de l'utilisateur dans
// une pile unique. Symétrique de structureSync (même baseline + même debounce),
// mais :
//  - capture même hors collab (pas de garde collabConnected) ;
//  - ignore les mutations distantes (remoteGuard) et nos propres undo/redo
//    (undoGuard) ;
//  - capture TOUTE édition locale quel que soit le mode (page, formulaire,
//    masque, configuration et sa cascade, éditions faites en aperçu) ;
//  - vide la pile et rebase au seul changement de DOCUMENT (un undo ne doit
//    jamais traverser les documents).
// Le blocage undo/redo pendant l'aperçu PDF est un gate d'action (isPreviewMode),
// pas de capture.
const CAPTURE_DEBOUNCE_MS = 150;

let storeApi = null; // { getState, subscribe }
let baseline = null;
let baselineDocId = null;
let push = null; // (entry) => void
let clear = null; // () => void
// Vrai entre la programmation d'une capture (édition locale) et son tir effectif
// (debounce). Sert à ne PAS laisser une mutation éphémère intercalée avancer la
// baseline et avaler ce diff en attente (cf. branche éphémère du subscriber).
let capturePending = false;

// Décide la capture et gère les effets de bord (rebase, vidage au changement de
// doc). Retourne true seulement si l'état courant doit être diffé et empilé.
const shouldCapture = (state) => {
  const docId = docIdOf(state);

  // Changement de document : l'historique ne doit jamais traverser les
  // documents (un undo y appliquerait un diff aberrant). Vider + rebaser.
  // On met la baseline à jour AVANT clear() : clear() notifie les listeners de
  // façon synchrone, qui rappellent ce gate — sans ça, re-entrée infinie.
  if (docId !== baselineDocId) {
    baseline = state;
    baselineDocId = docId;
    if (clear) clear();
    return false;
  }

  // Hors édition stable (liste, chargement) : pas de capture, on avance la
  // baseline en lockstep (vidage/repeuplement du store, pas une édition).
  if (!isStableEditState(state, baselineDocId)) {
    baseline = state;
    return false;
  }

  return true;
};

const captureNow = debounce(() => {
  capturePending = false;
  if (!push || !storeApi) return;
  const state = storeApi.getState();
  // Re-vérification au tir : l'état a pu changer pendant le debounce.
  if (!shouldCapture(state)) return;
  const diff = computeDiff(baseline, state);
  baseline = state;
  if (!diff) return;
  // Édition d'une box faite depuis la vue Variables (formulaire) : on note la
  // provenance pour que l'undo rouvre ce panneau et scrolle sur la box, plutôt
  // que de revenir à la page infinie (cf. navigateToFocus).
  const focus = focusFromDiff(diff.forward);
  if (focus?.type === 'box' && isVariablesView()) focus.fromVariablesView = true;
  push({
    forward: diff.forward,
    inverse: diff.inverse,
    focus,
  });
}, CAPTURE_DEBOUNCE_MS);

// Démarre la capture pour le document courant. `store` est l'API zustand
// (getState/subscribe) ; `pushEntry` empile une entrée et invalide le redo ;
// `clearHistory` vide les deux piles. Repart toujours d'un historique vide.
export const startUndoCapture = (store, pushEntry, clearHistory) => {
  storeApi = store;
  push = pushEntry;
  clear = clearHistory;
  baseline = store.getState();
  baselineDocId = docIdOf(baseline);
  if (clear) clear();

  const unsubscribe = store.subscribe((state) => {
    // Mutation distante (co-éditeur) ou notre propre application d'undo/redo :
    // jamais capturée, mais on avance la baseline pour que la prochaine édition
    // locale se diffe sur l'état à jour.
    if (isApplyingRemote() || isApplyingUndo()) {
      baseline = state;
      return;
    }
    // Mutation éphémère (mesure/layout au rendu, sélection) : idem, on n'empile
    // pas. MAIS si une capture réelle est déjà programmée (ex. add box suivi de
    // sa mesure au mount, dans la fenêtre de debounce), avancer la baseline
    // avalerait le diff en attente. On la garde alors : captureNow empilera
    // bien l'édition, la mesure se fondant dans la même entrée (sans danger —
    // l'undo d'un ajout supprime la box entière).
    if (isEphemeralMutation()) {
      if (!capturePending) baseline = state;
      return;
    }
    if (!shouldCapture(state)) return;
    capturePending = true;
    captureNow();
  });

  return () => {
    captureNow.cancel();
    capturePending = false;
    unsubscribe();
    storeApi = null;
    push = null;
    clear = null;
    baseline = null;
    baselineDocId = null;
  };
};

// Force l'empilement immédiat du diff en attente (appelé avant d'appliquer un
// undo/redo, pour que la session en cours devienne une entrée à part entière).
export const flushUndoCapture = () => captureNow.flush();
